import React, { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import AddInputs from "../Add/AddInputs"
import Button from "../buttons/Button"
import ButtonWithImg from "../buttons/ButtonWithImg"
import SecondaryButton from "../buttons/SecondaryButton"
import MainModel from "../Models/MainModel"
import { setCoordinates, setLocalErrors, setError } from "../../store/slices/geolocationSlice"
import { RootState } from "../../store"

interface CurrentLocationProps {
  handleClose: () => void
  getCurrentLocation: () => void
  handleGetAltitude: () => void
  inputCordinates: { latitude: number | null; longitude: number | null }
  setInputCordinates: React.Dispatch<React.SetStateAction<{ latitude: number | null; longitude: number | null }>>
}

export default function CurrentLocation({
  handleClose,
  getCurrentLocation,
  handleGetAltitude,
  inputCordinates,
  setInputCordinates
}: CurrentLocationProps) {
  const dispatch = useDispatch()
  const { coordinates, error, localErrors } = useSelector(
    (state: RootState) => state.geolocation,
  )
  const modalRef = useRef<HTMLDivElement>(null)
  
  // State to store previous values for undo functionality
  const [previousValues, setPreviousValues] = React.useState<Record<string, string>>({})

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [handleClose])

  const inputs = {
    "": {
      Latitude: {
        required: true,
        disabled: false,
        id: "latitude",
        type: "input",
        tabIndex: 0,
      },
      Longitude: {
        required: true,
        disabled: false,
        id: "longitude",
        type: "input",
        tabIndex: 1,
      },
    },
  }

  const onInputChange = (id: string, value: string) => {
    // Store previous value before making changes
    setPreviousValues(prev => ({
      ...prev,
      [id]: (inputCordinates as Record<string, unknown>)[id]?.toString() || ""
    }))

    // Clear the main error when user starts typing
    if (error) {
      dispatch(setError(null))
    }

    // Accept digits, an optional negative sign at start, and up to one decimal point
    const numericRegex = /^-?\d*\.?\d*$|^-$/

    if (value === "") {
      // Handle empty input case
      setInputCordinates({ ...inputCordinates, [id]: null })
      dispatch(setLocalErrors({ ...localErrors, [id]: "" }))
      return
    }

    // Check if the input matches our regex (valid number format)
    if (numericRegex.test(value)) {
      // For decimal inputs, check decimal places
      if (value.includes('.')) {
        const [, decimal] = value.split('.')
        if (decimal && decimal.length > 6) {
          return // Don't allow more than 6 decimal places
        }
        // Allow typing decimal point
        if (value.endsWith('.')) {
          setInputCordinates({ ...inputCordinates, [id]: value === '.' ? 0 : value })
        } else {
          const numericValue = parseFloat(value)
          setInputCordinates({ ...inputCordinates, [id]: numericValue })
        }
      } else if (value === '-') {
        setInputCordinates({ ...inputCordinates, [id]: value })
      } else {
        // No decimal point, safe to convert to number
        const numericValue = parseFloat(value)
        setInputCordinates({ ...inputCordinates, [id]: numericValue })
      }
      // Clear any existing errors for this field
      dispatch(setLocalErrors({ ...localErrors, [id]: "" }))
    } else {
      // Invalid format - don't update coordinates
      return
    }
  }

  // Handle undo functionality
  const handleUndo = (id: string) => {
    const previousValue = previousValues[id]
    if (previousValue !== undefined) {
      if (previousValue === "") {
        setInputCordinates({ ...inputCordinates, [id]: null })
      } else {
        const numericValue = parseFloat(previousValue)
        setInputCordinates({ ...inputCordinates, [id]: isNaN(numericValue) ? previousValue : numericValue })
      }
      // Clear any errors for this field
      dispatch(setLocalErrors({ ...localErrors, [id]: "" }))
    }
  }
  const validateCoordinates = () => {
    const errors: Record<string, string> = {}

    // Validate latitude
    if (inputCordinates.latitude === null) {
      errors.latitude = "Latitude is required"
    } else if (typeof inputCordinates.latitude === 'string' && inputCordinates.latitude === '-') {
      errors.latitude = "Please enter a valid number"
    } else if (isNaN(Number(inputCordinates.latitude))) {
      errors.latitude = "Please enter a valid number"
    } else {
      const latValue = Number(inputCordinates.latitude)
      if (latValue < -90 || latValue > 90) {
        errors.latitude = "Latitude must be between -90 and 90 degrees"
      }
    }

    // Validate longitude
    if (inputCordinates.longitude === null) {
      errors.longitude = "Longitude is required"
    } else if (typeof inputCordinates.longitude === 'string' && inputCordinates.longitude === '-') {
      errors.longitude = "Please enter a valid number"
    } else if (isNaN(Number(inputCordinates.longitude))) {
      errors.longitude = "Please enter a valid number"
    } else {
      const lonValue = Number(inputCordinates.longitude)
      if (lonValue < -180 || lonValue > 180) {
        errors.longitude = "Longitude must be between -180 and 180 degrees"
      }
    }

    dispatch(setLocalErrors(errors))
    return Object.keys(errors).length === 0
  }

  const handleGetAltitudeClick = () => {
    // Clear any existing errors first
    dispatch(setLocalErrors({ latitude: '', longitude: '' }))
    
    if (validateCoordinates()) {
      // Save coordinates to store before getting altitude
      if (inputCordinates.latitude !== null && inputCordinates.longitude !== null) {
        dispatch(setCoordinates({
          latitude: Number(inputCordinates.latitude),
          longitude: Number(inputCordinates.longitude)
        }))
      }
      handleGetAltitude()
      handleClose()
    } else {
      // Validation failed - errors are already set by validateCoordinates
      // Focus on the first field with an error
      const firstErrorField = Object.keys(localErrors).find(key => localErrors[key])
      if (firstErrorField) {
        const inputElement = document.querySelector(`input[id="${firstErrorField}"]`) as HTMLInputElement
        if (inputElement) {
          inputElement.focus()
        }
      }
    }
  }

  const handleUseCurrentLocation = () => {
    // Check if there's an existing error and add it to latitude localErrors
    if (error) {
      dispatch(setLocalErrors({
        ...localErrors,
        latitude: error
      }))
      // Clear the main error since we're moving it to field-specific error
      dispatch(setError(null))
    }

    // Call the original getCurrentLocation function
    getCurrentLocation()
  }

  useEffect(() => {
    if (coordinates.latitude !== null && coordinates.longitude !== null) {
      setInputCordinates({
        latitude: coordinates.latitude,
        longitude: coordinates.longitude
      })
    } else {
      setInputCordinates({ latitude: null, longitude: null })
    }
    // Clear any existing errors when component mounts or coordinates change
    dispatch(setLocalErrors({ latitude: '', longitude: '' }))
  }, [coordinates, dispatch, setInputCordinates])

  // Watch for errors and move them to latitude field when they occur
  useEffect(() => {
    if (error) {
      dispatch(setLocalErrors({
        ...localErrors,
        latitude: error
      }))
      // Clear the main error since we're moving it to field-specific error
      dispatch(setError(null))
    }
  }, [error, dispatch, localErrors])

  return (
    <MainModel modelWidth={"w-[744px]"} padding=" ">
      <div ref={modalRef} className="p-6">
        <div className={`flex items-center justify-between pb-3.5 border-b mb-2`}>
          <h1 className="text-heading5">Current Location</h1>
          <ButtonWithImg
            testId="use_my_current_location"
            ImgSrc="map-pin"
            buttonName="Use my current location"
            className="px-4"
            buttonOnclick={handleUseCurrentLocation}
          />
        </div>
        <AddInputs
          inputs={inputs}
          totalInputCount={2}
          inputValues={{
            latitude: inputCordinates?.latitude?.toString() || "",
            longitude: inputCordinates?.longitude?.toString() || "",
          }}
          errors={localErrors}
          onInputChange={onInputChange}
          previousValues={previousValues}
          onUndo={handleUndo}
        />
        <div className={`flex gap-6 ${Object.values(localErrors).some(error => error && error.trim() !== '') ? 'mt-0.5' : 'mt-10'}`}>
          <SecondaryButton
            buttonName={"Cancel"}
            className="px-4 py-2 h-fit"
            handleClick={handleClose}
            testId="cancel-btn"
            buttonDisabled={false}
          />
          <Button
            buttonName={"Get Altitude"}
            className="py-2.5 h-fit px-4 rounded-[6px]"
            textSize={"text-large"}
            buttonOnclick={handleGetAltitudeClick}
            testId={`get-altitude`}
            loading={false}
          />
        </div>
      </div>
    </MainModel>
  )
}